import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, ExternalLink, HeartHandshake, Stethoscope, ShieldCheck,
  Car, GraduationCap, CheckCircle2, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

/**
 * Each benefit block:
 *  - icon (lucide) + accent color (kept within the FR INTERIM palette)
 *  - short intro sentence
 *  - bullet list of concrete services
 *  - one or several external links opening in a new tab
 */
const BENEFITS = [
  {
    key: "fastt",
    icon: HeartHandshake,
    kicker: "Vie quotidienne",
    title: "FASTT — des services pour faciliter votre quotidien",
    intro:
      "Le FASTT accompagne les salariés intérimaires dans de nombreux domaines de la vie quotidienne. Selon votre situation et les conditions d'éligibilité, vous pouvez bénéficier de services et d'aides concernant notamment :",
    bullets: [
      "le logement",
      "la mobilité",
      "la garde d'enfants",
      "la santé",
      "l'accompagnement social et budgétaire",
    ],
    links: [{ label: "Découvrir les services du FASTT", href: "https://www.fastt.org/" }],
  },
  {
    key: "sante",
    icon: Stethoscope,
    kicker: "Complémentaire santé",
    title: "Votre mutuelle santé",
    intro:
      "En tant que salarié intérimaire, vous pouvez bénéficier d'une complémentaire santé dédiée aux travailleurs temporaires, sous réserve des conditions applicables à votre situation. Cette couverture peut notamment prendre en charge une partie de vos frais de santé :",
    bullets: [
      "consultations médicales",
      "pharmacie",
      "hospitalisation",
      "optique",
      "soins dentaires",
    ],
    links: [{ label: "En savoir plus sur votre mutuelle santé", href: "https://www.interimairessante.fr/" }],
  },
  {
    key: "prevoyance",
    icon: ShieldCheck,
    kicker: "Protection",
    title: "La prévoyance",
    intro:
      "La prévoyance vous accompagne dans certaines situations importantes de la vie. Selon les conditions applicables, elle peut vous apporter une protection en cas de :",
    bullets: ["arrêt de travail", "accident", "incapacité", "invalidité", "décès"],
    links: [{ label: "Découvrir vos garanties", href: "https://www.interimairesprevoyance.fr/accueil" }],
  },
  {
    key: "mobilite",
    icon: Car,
    kicker: "Déplacements & permis",
    title: "Des solutions pour votre mobilité",
    intro:
      "La mobilité est essentielle pour accéder à l'emploi et se rendre sur votre lieu de mission. Selon votre situation, des solutions peuvent vous être proposées pour faciliter vos déplacements, notamment :",
    bullets: [
      "aide à la mobilité",
      "location de véhicule",
      "accompagnement dans vos déplacements",
      "solutions liées au permis de conduire",
    ],
    links: [
      { label: "Mobilités 77", href: "https://mobilites77.org/" },
      { label: "Mobilité Emploi", href: "https://www.mobilite-emploi.fr/" },
      { label: "Plateforme Mobilité Rhône-Alpes", href: "https://www.plateformemobilite-ra.fr/mobilite-07-26.html" },
    ],
  },
  {
    key: "formation",
    icon: GraduationCap,
    kicker: "Montée en compétences",
    title: "La formation pour développer vos compétences",
    intro:
      "Chaque mission peut être une occasion de développer votre expérience et vos compétences. Des dispositifs de formation peuvent vous permettre de :",
    bullets: [
      "acquérir de nouvelles compétences",
      "obtenir une qualification",
      "préparer une évolution professionnelle",
      "faciliter votre retour à l'emploi",
    ],
    links: [],
    footnote: "Rapprochez-vous de votre agence FR INTERIM pour connaître les dispositifs éligibles.",
  },
];

export default function AvantagesPage() {
  return (
    <main data-testid="avantages-page">
      <SEO
        title="Avantages intérimaires — FR INTERIM"
        description="FASTT, mutuelle santé, prévoyance, mobilité, formation : découvrez les avantages et services dédiés aux salariés intérimaires accompagnés par FR INTERIM."
        path="/avantages"
      />

      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-[#E5CD96] text-xs font-bold uppercase tracking-[0.2em] mb-4"
            >
              Vos avantages
            </motion.p>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6"
            >
              Vos avantages en tant qu&apos;intérimaire
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-gray-100 leading-relaxed mb-3">
              L&apos;intérim, c&apos;est aussi <span className="text-[#E5CD96] font-semibold">de nombreux avantages</span>.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="text-base text-gray-200 leading-relaxed">
              En travaillant avec FR INTERIM, vous bénéficiez de l&apos;accompagnement de votre agence, mais également de dispositifs
              et services spécialement dédiés aux salariés intérimaires. Découvrez les solutions qui peuvent vous accompagner
              dans votre vie professionnelle et personnelle.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Benefits list — alternating layout */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            const isEven = i % 2 === 0;
            return (
              <motion.article
                key={b.key}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                custom={i}
                variants={fadeUp}
                data-testid={`benefit-${b.key}`}
                className={`bg-white border border-gray-200 grid md:grid-cols-5 overflow-hidden card-hover ${isEven ? "" : "md:[&>div:first-child]:order-2"}`}
              >
                {/* Icon column — gradient background reinforces the brand */}
                <div className="md:col-span-2 relative bg-gradient-to-br from-[#662483] to-[#0069B4] flex flex-col items-start justify-center gap-4 p-8 md:p-10">
                  <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#E5CD96]">
                    {b.kicker}
                  </span>
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                    <Icon className="w-8 h-8 text-white" strokeWidth={1.75} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-white leading-tight">
                    {b.title}
                  </h2>
                </div>

                {/* Text column */}
                <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">{b.intro}</p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-5">
                    {b.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2 text-sm text-[#3F3F3F]">
                        <CheckCircle2 className="w-4 h-4 text-[#662483] shrink-0 mt-0.5" />
                        <span className="capitalize-first">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  {b.footnote && (
                    <p className="text-xs italic text-gray-500 mb-4 border-l-2 border-[#E5CD96] pl-3">
                      {b.footnote}
                    </p>
                  )}
                  {b.links.length > 0 && (
                    <div className="flex flex-wrap gap-3">
                      {b.links.map((l) => (
                        <a
                          key={l.href}
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`benefit-${b.key}-link`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-[#662483] border-b-2 border-transparent hover:border-[#662483] transition-colors group"
                        >
                          {l.label}
                          <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Closing block */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="border-l-4 border-[#662483] bg-[#F0E9F3] p-8 md:p-12"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3">
              Notre engagement
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl font-extrabold text-[#3F3F3F] mb-4">
              FR INTERIM vous accompagne
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-base text-gray-700 leading-relaxed mb-2">
              Votre mission ne se limite pas à votre poste de travail. FR INTERIM est à vos côtés pour vous informer et vous
              orienter vers les dispositifs et services auxquels vous pouvez avoir accès en tant que salarié intérimaire.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="text-base text-[#3F3F3F] font-semibold flex items-start gap-2 mb-6">
              <span className="text-[#662483]">👉</span>
              Une question sur vos droits ou vos avantages ? Notre équipe est à votre écoute.
            </motion.p>
            <Link to="/contact">
              <Button
                className="rounded-none bg-gradient-to-r from-[#662483] to-[#0069B4] text-white px-6 py-3 font-semibold"
                data-testid="avantages-contact-cta"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Nous contacter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Register CTA */}
      <section className="brand-gradient py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            Prêt(e) à rejoindre FR INTERIM&nbsp;?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Inscrivez-vous en quelques minutes et bénéficiez de tous ces avantages dès votre première mission.
          </p>
          <Link to="/auth">
            <Button
              className="bg-[#E5CD96] text-[#3F3F3F] hover:bg-[#B8A478] font-semibold px-8 py-3 rounded-none text-base"
              data-testid="avantages-register-cta"
            >
              S&apos;inscrire maintenant <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
